import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTools, 
  faEye, 
  faSearch,
  faSpinner,
  faExclamationTriangle,
  faCopy,
  faCode,
  faKey,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const ToolsTab = ({ agentId }) => {
  const { token } = useAuth();
  const [tools, setTools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTool, setSelectedTool] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Fetch tools
  useEffect(() => {
    fetchTools();
  }, [agentId, token]);

  const fetchTools = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/agents/${agentId}/settings/tools`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTools(data.tools || []);
      } else {
        throw new Error(`Failed to fetch tools: ${response.status}`);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching tools:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewTool = async (toolId) => {
    try {
      const response = await fetch(`/api/agents/${agentId}/settings/tools/${toolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedTool(data.tool);
        setShowViewModal(true);
      } else {
        toast.error('Failed to load tool details');
      }
    } catch (err) {
      toast.error('Error loading tool details');
      console.error('Error:', err);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard!`);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const copySchema = async (schema) => {
    try {
      const formattedSchema = JSON.stringify(schema, null, 2);
      await navigator.clipboard.writeText(formattedSchema);
      toast.success('Schema copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy schema');
    }
  };

  const filteredTools = tools.filter(tool =>
    tool.tool_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.backend_implementation_ref?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getToolCategory = (toolName) => {
    if (toolName.includes('netsuite')) return 'NetSuite';
    if (toolName.includes('po')) return 'Purchase Orders';
    if (toolName.includes('bill')) return 'Bill Processing';
    if (toolName.includes('vendor')) return 'Vendor Management';
    if (toolName.includes('item')) return 'Item Management';
    return 'General';
  };

  if (isLoading) {
    return (
      <div className="tools-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <p>Loading tools...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tools-error">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <p>Error loading tools: {error}</p>
        <button onClick={fetchTools}>Retry</button>
      </div>
    );
  }

  return (
    <div className="tools-tab">
      {/* Header */}
      <div className="tools-header">
        <div className="header-info">
          <h2>Tool Definitions</h2>
          <p>Available tools for this agent ({tools.length} total)</p>
          <div className="tools-info-banner">
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>These are the NetSuite integration tools available to the Bill Processing Agent</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="tools-search">
        <div className="search-input-wrapper">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search tools by name, description, or implementation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Tools Grid */}
      <div className="tools-grid">
        {filteredTools.length === 0 ? (
          <div className="empty-state">
            <h3>No tools found</h3>
            <p>
              {searchQuery 
                ? `No tools match "${searchQuery}"`
                : 'No tools available for this agent'
              }
            </p>
          </div>
        ) : (
          filteredTools.map(tool => (
            <div key={tool.tool_definition_id} className="tool-card">
              <div className="tool-card-header">
                <div className="tool-info">
                  <div className="tool-title">
                    <h3>{tool.tool_name}</h3>
                    <span className="tool-category">{getToolCategory(tool.tool_name)}</span>
                  </div>
                  <p className="tool-description">
                    {tool.description}
                  </p>
                </div>
                <div className="tool-actions">
                  <button
                    className="action-btn view-btn"
                    onClick={() => handleViewTool(tool.tool_definition_id)}
                    title="View Tool Details"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                  <button
                    className="action-btn copy-btn"
                    onClick={() => copyToClipboard(tool.tool_definition_id, 'Tool ID')}
                    title="Copy Tool ID"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                </div>
              </div>

              <div className="tool-card-body">
                <div className="tool-implementation">
                  <div className="impl-item">
                    <FontAwesomeIcon icon={faCode} className="impl-icon" />
                    <div className="impl-details">
                      <span className="impl-label">Implementation:</span>
                      <code className="impl-ref">{tool.backend_implementation_ref}</code>
                    </div>
                  </div>
                  
                  {tool.required_auth_service_name && (
                    <div className="impl-item">
                      <FontAwesomeIcon icon={faKey} className="impl-icon" />
                      <div className="impl-details">
                        <span className="impl-label">Auth Service:</span>
                        <span className="auth-service">{tool.required_auth_service_name}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="tool-card-footer">
                  <div className="tool-date">
                    <small>Updated: {formatDate(tool.updated_at)}</small>
                  </div>
                  <div className="schema-preview">
                    <button
                      className="schema-btn"
                      onClick={() => copySchema(tool.parameters_json_schema)}
                      title="Copy Schema"
                    >
                      <FontAwesomeIcon icon={faCode} />
                      Copy Schema
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* View Tool Modal */}
      {showViewModal && selectedTool && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content tool-view-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Tool Details: {selectedTool.tool_name}</h2>
              <button className="close-btn" onClick={() => setShowViewModal(false)}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="tool-section">
                <h3>Basic Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Tool Name:</label>
                    <span>{selectedTool.tool_name}</span>
                  </div>
                  <div className="info-item">
                    <label>Category:</label>
                    <span className="tool-category">{getToolCategory(selectedTool.tool_name)}</span>
                  </div>
                  <div className="info-item">
                    <label>Description:</label>
                    <span>{selectedTool.description}</span>
                  </div>
                  <div className="info-item">
                    <label>Tool ID:</label>
                    <div className="copyable-field">
                      <code className="tool-id">{selectedTool.tool_definition_id}</code>
                      <button 
                        className="copy-small-btn"
                        onClick={() => copyToClipboard(selectedTool.tool_definition_id, 'Tool ID')}
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="tool-section">
                <h3>Implementation Details</h3>
                <div className="impl-details-grid">
                  <div className="impl-item-detail">
                    <label>Backend Implementation:</label>
                    <code className="impl-code">{selectedTool.backend_implementation_ref}</code>
                  </div>
                  {selectedTool.required_auth_service_name && (
                    <div className="impl-item-detail">
                      <label>Required Auth Service:</label>
                      <span className="auth-service-detail">{selectedTool.required_auth_service_name}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="tool-section">
                <h3>Parameters Schema</h3>
                <div className="schema-section">
                  <div className="schema-header">
                    <span>JSON Schema Definition</span>
                    <button 
                      className="copy-schema-btn"
                      onClick={() => copySchema(selectedTool.parameters_json_schema)}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                      Copy Schema
                    </button>
                  </div>
                  <div className="schema-display">
                    <pre>{JSON.stringify(selectedTool.parameters_json_schema, null, 2)}</pre>
                  </div>
                </div>
              </div>

              <div className="tool-section">
                <h3>Metadata</h3>
                <div className="metadata-grid">
                  <div className="metadata-item">
                    <label>Created:</label>
                    <span>{formatDate(selectedTool.created_at)}</span>
                  </div>
                  <div className="metadata-item">
                    <label>Last Updated:</label>
                    <span>{formatDate(selectedTool.updated_at)}</span>
                  </div>
                  <div className="metadata-item">
                    <label>Creator:</label>
                    <span>{selectedTool.creator_user_id || 'System'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
              <button 
                className="btn-primary"
                onClick={() => copySchema(selectedTool.parameters_json_schema)}
              >
                <FontAwesomeIcon icon={faCopy} />
                Copy Schema
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolsTab;